import { useEffect, useState } from 'react';
import {
  useNavigate,
  useLocation,
  type NavigateFunction,
} from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import {
  checkAccess,
  invalidateAccessCache,
  type AccessResponse,
} from '@/core/access-check';

// Pages a banned (or IP-banned) user is still allowed to reach.
// /banned is the guard page itself (renders the wallet CTA inline
// when pending funds exist). /wallet is reachable from that CTA to
// configure payout details. /redemption is the takeover screen for
// users in the 24h Redemption Day window (fase 28). Non-banned /
// non-redeeming users hitting these get kicked back via resolveRedirect.
const BANNED_ALLOWED = new Set<string>(['/banned', '/wallet', '/redemption']);
const STATUS_POLL_MS = 60_000;

function resolveRedirect(res: AccessResponse, pathname: string): string | null {
  if (res.redemption_active) {
    return pathname === '/redemption' ? null : '/redemption';
  }
  if (res.access === 'ok') {
    if (pathname === '/banned' || pathname === '/redemption') return '/';
    return null;
  }
  if (BANNED_ALLOWED.has(pathname)) return null;
  return '/banned';
}

async function runCheck(
  pathname: string,
  nav: NavigateFunction,
  isCancelled: () => boolean
): Promise<void> {
  try {
    const res = await checkAccess();
    if (isCancelled()) return;
    const to = resolveRedirect(res, pathname);
    if (to) nav(to, { replace: true });
  } catch {
    /* network errors non-fatal — fall back to store-based guards */
  }
}

// Poll every minute on any route so a server-side ban (admin override,
// test-phase cron) or unban flips the user without needing a manual
// refresh or a navigation. refreshAuth keeps user.account_status in
// sync across the UI; checkAccess drives the actual redirect.
type PollArgs = {
  enabled: boolean;
  pathname: string;
  nav: NavigateFunction;
  refreshAuth: () => Promise<void>;
};

function useStatusPoll({
  enabled,
  pathname,
  nav,
  refreshAuth,
}: PollArgs): void {
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const id = setInterval(() => {
      void refreshAuth();
      invalidateAccessCache();
      void runCheck(pathname, nav, () => cancelled);
    }, STATUS_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [enabled, pathname, nav, refreshAuth]);
}

export function useAccessGuard(): { checking: boolean } {
  const { isAuthenticated, isLoading } = useAuthStore();
  const refreshAuth = useAuthStore((s) => s.refreshAuth);
  const nav = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) return setChecking(false);
    let cancelled = false;
    void runCheck(location.pathname, nav, () => cancelled).finally(() => {
      if (!cancelled) setChecking(false);
    });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isLoading, location.pathname, nav]);

  useStatusPoll({
    enabled: isAuthenticated,
    pathname: location.pathname,
    nav,
    refreshAuth,
  });

  return { checking };
}
